import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Chip,
  Menu,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  X,
  Share2,
  Copy,
  Edit,
  Trash2,
  MoreVertical,
  Lock,
  Users,
  Globe,
} from 'lucide-react';
import { useAuth } from '../../../../AuthProvider';

const TemplatesManager = ({ onClose, onApplyTemplate, onOpenView }) => {
  const { getAccessToken } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [views, setViews] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const accessToken = getAccessToken();

        const [templatesResponse, viewsResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_NODE_URL}/discover/templates`, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }),
          fetch(`${process.env.REACT_APP_NODE_URL}/discover/views`, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }),
        ]);

        const templatesJson = await templatesResponse.json();
        const viewsJson = await viewsResponse.json();

        if (!templatesResponse.ok || !templatesJson.status) {
          throw new Error(templatesJson.message || 'Failed to load templates');
        }
        if (!viewsResponse.ok || !viewsJson.status) {
          throw new Error(viewsJson.message || 'Failed to load views');
        }

        setTemplates(templatesJson.data || []);
        setViews(viewsJson.data || []);
        console.log('[Discover] Loaded templates and views');
      } catch (error) {
        console.error('[Discover] Error loading templates/views:', error);
        setLoadError(error.message || 'Failed to load templates and views');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [getAccessToken]);

  const filteredTemplates = templates.filter(t => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (t.name || '').toLowerCase().includes(query) ||
      (t.description || '').toLowerCase().includes(query)
    );
  });

  const filteredViews = views.filter(v => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (v.name || '').toLowerCase().includes(query) ||
      (v.searchAddress || '').toLowerCase().includes(query)
    );
  });

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '0.75rem',
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
              Templates & Saved Views
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: '#6B7280',
                marginTop: '0.25rem',
              }}
            >
              Load or manage your saved configurations
            </Typography>
          </Box>
          <IconButton onClick={onClose} size='small'>
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 0 }}>
        <Box
          sx={{
            padding: { xs: '1rem', md: '1.5rem' },
            borderBottom: '1px solid #E5E7EB',
          }}
        >
          <TextField
            fullWidth
            placeholder='Search templates and views...'
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            size='small'
            InputProps={{
              startAdornment: (
                <Search
                  size={16}
                  style={{ marginRight: '0.5rem', color: '#6B7280' }}
                />
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#FFFFFF',
                borderRadius: '0.75rem',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#D1D5DB',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#39585C',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#39585C',
                },
              },
            }}
          />
        </Box>

        <Box sx={{ borderBottom: '1px solid #E5E7EB' }}>
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                color: '#6B7280',
                '&.Mui-selected': {
                  color: '#39585C',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#39585C',
              },
            }}
          >
            <Tab
              label={`Templates (${templates.length})`}
              sx={{ textTransform: 'none' }}
            />
            <Tab
              label={`Saved Views (${views.length})`}
              sx={{ textTransform: 'none' }}
            />
          </Tabs>
        </Box>

        <Box
          sx={{
            maxHeight: '60vh',
            overflowY: 'auto',
            padding: { xs: '1rem', md: '1.5rem' },
          }}
        >
          {loading && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                paddingY: '2rem',
              }}
            >
              <CircularProgress size={24} sx={{ color: '#39585C' }} />
            </Box>
          )}

          {loadError && !loading && (
            <Box
              sx={{
                paddingY: '1rem',
              }}
            >
              <Typography sx={{ color: '#B91C1C', fontSize: '0.875rem' }}>
                {loadError}
              </Typography>
            </Box>
          )}

          {!loading && !loadError && selectedTab === 0 && (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
            >
              {filteredTemplates.map(template => (
                <TemplateCard
                  key={template._id}
                  template={template}
                  onApply={() => {
                    onApplyTemplate(template);
                    onClose();
                  }}
                />
              ))}
              {filteredTemplates.length === 0 && (
                <Box sx={{ textAlign: 'center', paddingY: '3rem' }}>
                  <Typography sx={{ color: '#6B7280' }}>
                    No templates found
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {!loading && !loadError && selectedTab === 1 && (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
            >
              {filteredViews.map(view => (
                <ViewCard
                  key={view._id}
                  view={view}
                  onOpen={() => {
                    onOpenView(view);
                    onClose();
                  }}
                />
              ))}
              {filteredViews.length === 0 && (
                <Box sx={{ textAlign: 'center', paddingY: '3rem' }}>
                  <Typography sx={{ color: '#6B7280' }}>
                    No saved views found
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{ padding: '1rem 1.5rem', borderTop: '1px solid #E5E7EB' }}
      >
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function TemplateCard({ template, onApply }) {
  const [menuAnchor, setMenuAnchor] = useState(null);

  const getVisibilityIcon = visibility => {
    switch (visibility) {
      case 'private':
        return <Lock size={12} />;
      case 'team':
        return <Users size={12} />;
      case 'org':
        return <Globe size={12} />;
      default:
        return null;
    }
  };

  const visibility = template.visibility || 'private';
  const ownerLabel = template.userEmail || 'You';

  return (
    <>
      <Card
        sx={{
          padding: '1rem',
          borderRadius: '0.75rem',
          '&:hover': { boxShadow: 2 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'start',
            justifyContent: 'space-between',
            gap: '1rem',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.25rem',
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                {template.name}
              </Typography>
              <Chip
                icon={getVisibilityIcon(visibility)}
                label={visibility}
                size='small'
                variant='outlined'
                sx={{
                  height: '20px',
                  fontSize: '0.625rem',
                }}
              />
            </Box>
            {template.description && (
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  color: '#6B7280',
                  marginBottom: '0.75rem',
                }}
              >
                {template.description}
              </Typography>
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: '0.5rem', md: '1rem' },
                fontSize: { xs: '0.625rem', md: '0.75rem' },
                color: '#6B7280',
                flexWrap: 'wrap',
              }}
            >
              <Typography>By {ownerLabel}</Typography>
              <Typography sx={{ display: { xs: 'none', sm: 'inline' } }}>
                •
              </Typography>
              <Typography>
                Updated{' '}
                {template.updatedAt
                  ? new Date(template.updatedAt).toLocaleDateString()
                  : ''}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
            <Button
              size='small'
              variant='contained'
              onClick={onApply}
              sx={{
                textTransform: 'none',
                backgroundColor: '#39585C',
                '&:hover': {
                  backgroundColor: '#2d4649',
                },
                fontSize: { xs: '0.75rem', md: '0.875rem' },
              }}
            >
              Apply
            </Button>
            <IconButton
              size='small'
              onClick={e => setMenuAnchor(e.currentTarget)}
              sx={{ padding: '0.25rem' }}
            >
              <MoreVertical size={16} />
            </IconButton>
          </Box>
        </Box>
      </Card>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => setMenuAnchor(null)}>
          <Share2 size={16} style={{ marginRight: '0.5rem' }} />
          Share
        </MenuItem>
        <MenuItem onClick={() => setMenuAnchor(null)}>
          <Copy size={16} style={{ marginRight: '0.5rem' }} />
          Duplicate
        </MenuItem>
        <MenuItem onClick={() => setMenuAnchor(null)}>
          <Edit size={16} style={{ marginRight: '0.5rem' }} />
          Rename
        </MenuItem>
        <MenuItem onClick={() => setMenuAnchor(null)} sx={{ color: '#EF4444' }}>
          <Trash2 size={16} style={{ marginRight: '0.5rem' }} />
          Delete
        </MenuItem>
      </Menu>
    </>
  );
}

function ViewCard({ view, onOpen }) {
  const [menuAnchor, setMenuAnchor] = useState(null);

  return (
    <>
      <Card
        sx={{
          padding: '1rem',
          borderRadius: '0.75rem',
          '&:hover': { boxShadow: 2 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'start',
            justifyContent: 'space-between',
            gap: '1rem',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.25rem',
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                {view.name}
              </Typography>
              <Chip
                label={view.searchAddress}
                size='small'
                sx={{
                  height: '20px',
                  fontSize: '0.625rem',
                  backgroundColor: '#F3F4F6',
                }}
              />
            </Box>
            {view.description && (
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  color: '#6B7280',
                  marginBottom: '0.75rem',
                }}
              >
                {view.description}
              </Typography>
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: '0.5rem', md: '1rem' },
                fontSize: { xs: '0.625rem', md: '0.75rem' },
                color: '#6B7280',
                flexWrap: 'wrap',
              }}
            >
              <Typography>By {view.userEmail || 'You'}</Typography>
              <Typography sx={{ display: { xs: 'none', sm: 'inline' } }}>
                •
              </Typography>
              <Typography>
                Updated{' '}
                {view.updatedAt
                  ? new Date(view.updatedAt).toLocaleDateString()
                  : ''}
              </Typography>
              <Typography sx={{ display: { xs: 'none', sm: 'inline' } }}>
                •
              </Typography>
              <Typography>
                {view.radius ? `${view.radius} mi radius` : ''}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
            <Button
              size='small'
              variant='contained'
              onClick={onOpen}
              sx={{
                textTransform: 'none',
                backgroundColor: '#39585C',
                '&:hover': {
                  backgroundColor: '#2d4649',
                },
                fontSize: { xs: '0.75rem', md: '0.875rem' },
              }}
            >
              Open
            </Button>
            <IconButton
              size='small'
              onClick={e => setMenuAnchor(e.currentTarget)}
              sx={{ padding: '0.25rem' }}
            >
              <MoreVertical size={16} />
            </IconButton>
          </Box>
        </Box>
      </Card>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => setMenuAnchor(null)}>
          <Share2 size={16} style={{ marginRight: '0.5rem' }} />
          Share Link
        </MenuItem>
        <MenuItem onClick={() => setMenuAnchor(null)}>
          <Copy size={16} style={{ marginRight: '0.5rem' }} />
          Duplicate
        </MenuItem>
        <MenuItem onClick={() => setMenuAnchor(null)} sx={{ color: '#EF4444' }}>
          <Trash2 size={16} style={{ marginRight: '0.5rem' }} />
          Delete
        </MenuItem>
      </Menu>
    </>
  );
}

export default TemplatesManager;
